import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { Stack, SvgIcon } from "@mui/material";
import React, { useState } from "react";
import * as frontService from "source/models/services/frontService";
import { UserProfileDialog } from "source/components/user/UserProfileDialog/UserProfileDialog";
import { IAppSessionState } from "source/models/store/storeTypes";
import { SvgFlagEn, SvgFlagFi } from "source/shared/icons";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { ToolButtonDropdown } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { CultureCode } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";

export interface IUserMenuProp {
  appState: IAppSessionState;
  onLogout: () => void;
  onNavigateOutCheck?: () => Promise<boolean>;
  onSetCulture: (culture: string) => void;
  onRefreshSessionState: () => void;
}

export const UserMenu = (props: IUserMenuProp) => {
    const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);
    
    const sessionState = props.appState?.state;

    const handleUserProfileClick = () => {
        if (!props.appState?.state?.canUseSystem()) return;
        setShowUserProfileDialog(true);
    };

    const handleUserProfileDialogOk = () => {
        setShowUserProfileDialog(false);
    };

    const handleUserProfileDialogCancel = () => {
        setShowUserProfileDialog(false);
    };

    const canNavigateOut = async (): Promise<boolean> => {
        if (!props.onNavigateOutCheck) return Base.getPromiseResult(true);
        return await props.onNavigateOutCheck();
    };

    const handleLogOutClick = () => {
        canNavigateOut().then((result) => {
            if (!result) return;
            if (!props.onLogout) return;
            props.onLogout();
        });
    };

    const changeCompany = async (companyId: string) => {
        const result = await AppUtils.callService(() => frontService.setActiveCompany(companyId));
        if (!result) return;
        window.location.href = appConfig.rootUrl + "Front";
    };

    return (
        <>
            <Stack spacing={1} direction="row" sx={{ marginRight: "1.5rem" }}>
                <ToolButtonDropdown
                    variant="text"
                    fullWidth
                    title={
                        appConfig.culture === "fi"
                            ? Translations.LanguageFinnish
                            : Translations.LanguageEnglish
                    }
                    icon={
                        <SvgIcon fontSize="small">
                            {appConfig.culture === "fi" ? <SvgFlagFi /> : <SvgFlagEn />}
                        </SvgIcon>
                    }
                    actions={[
                        {
                            title: Translations.LanguageFinnish,
                            onClick: () => props.onSetCulture(CultureCode.Fi),
                            icon: (
                                <SvgIcon fontSize="small">
                                    <SvgFlagFi />
                                </SvgIcon>
                            ),
                            disabled: !sessionState.canUseSystem(),
                        },
                        {
                            title: Translations.LanguageEnglish,
                            onClick: () => props.onSetCulture(CultureCode.En),
                            icon: (
                                <SvgIcon fontSize="small">
                                    <SvgFlagEn />
                                </SvgIcon>
                            ),
                        },
                    ]}
                />
                {!sessionState.isSuperAdmin && sessionState.allowedCompanies.length > 1 &&
                    <ToolButtonDropdown
                        variant="text"
                        fullWidth
                        title={sessionState.companyName}
                        icon={<AvatarImage
                            size={20}
                            name={sessionState.companyName}
                            image={`data:image/png;base64,${sessionState.companyLogoSquare}`}
                        />}
                        actions={sessionState.allowedCompanies.map((company) => {
                            return {
                                title: company.name,
                                icon: <AvatarImage
                                    size={20}
                                    name={company.name}
                                    image={company.logo}
                                />,
                                onClick: () => changeCompany(company.id)
                            };
                        })}
                    />
                }
                <ToolButtonDropdown
                    variant="text"
                    fullWidth
                    title={sessionState.name}
                    icon={
                        <AvatarImage
                            image={`data:image/png;base64,${sessionState.image}`}
                            name={sessionState.name}
                            size={20}
                        />
                    }
                    actions={[
                        {
                            title: Translations.UserProfile,
                            onClick: handleUserProfileClick,
                            icon: <PersonIcon fontSize="small" />,
                            disabled: !sessionState.canUseSystem(),
                        },
                        {
                            title: Translations.LogOut,
                            onClick: handleLogOutClick,
                            icon: <LogoutIcon fontSize="small" />,
                        },
                    ]}
                />
            </Stack>
            {showUserProfileDialog && (
                <UserProfileDialog
                    sessionState={sessionState}
                    onRefreshSessionState={props.onRefreshSessionState}
                    onOk={handleUserProfileDialogOk}
                    onCancel={handleUserProfileDialogCancel}
                />
            )}
        </>
    );
};
